import { OutsideAppLink } from "@smartmanager/shell-ui/components/utils/OutsideAppLink";
import { PlatformLink } from "@smartmanager/shell-ui/components/utils/PlatformLink";
import React from "react";

export function slugify(v: string) {
  return v
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}

const LEGAL_LINK_CLASS = "underline decoration-white/25 underline-offset-2 transition-colors hover:text-white";

function renderLegalInlineText(value: string) {
  const elements: React.ReactNode[] = [];
  // Absolute https links open in Smart Manager's own browser (PlatformLink),
  // mailto links open the mail app with their one address (OutsideAppLink),
  // and site-relative paths and in-page anchors stay in the current tab.
  const linkPattern = /\[([^\]]+)\]\(((?:https?:\/\/|\/|#|mailto:)[^)\s]*)\)/g;
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = linkPattern.exec(value)) !== null) {
    if (match.index > cursor) elements.push(value.slice(cursor, match.index));
    const href = match[2];
    const key = `${match.index}-${href}`;
    elements.push(
      /^https?:\/\//.test(href) ? (
        <PlatformLink key={key} href={href} className={LEGAL_LINK_CLASS}>
          {match[1]}
        </PlatformLink>
      ) : href.startsWith("mailto:") ? (
        <OutsideAppLink key={key} href={href} reason="mail" appCopy className={LEGAL_LINK_CLASS}>
          {match[1]}
        </OutsideAppLink>
      ) : (
        // in-app-browser exception: a site-relative page or in-page anchor written in the repo's legal copy stays in this tab.
        <a key={key} href={href} className={LEGAL_LINK_CLASS}>
          {match[1]}
        </a>
      ),
    );
    cursor = match.index + match[0].length;
  }

  if (cursor < value.length) elements.push(value.slice(cursor));
  return elements.length > 0 ? elements : value;
}

export function renderLegalContent(raw: string, accentRgb: string) {
  const lines = raw
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  const elements: React.ReactNode[] = [];
  let listItems: string[] = [];

  const flushList = () => {
    if (listItems.length === 0) return;
    elements.push(
      <ul key={`ul-${elements.length}`} className="my-4 space-y-3 pl-1">
        {listItems.map((item, i) => (
          <li key={i} className="flex items-start gap-3 text-[15px] leading-[1.85] text-white/80">
            <span
              className="mt-[10px] h-1 w-1 shrink-0 rounded-full"
              style={{ background: `rgba(${accentRgb}, 0.6)` }}
            />
            <span>{renderLegalInlineText(item)}</span>
          </li>
        ))}
      </ul>
    );
    listItems = [];
  };

  lines.forEach((line, i) => {
    if (line.startsWith("- ")) {
      listItems.push(line.slice(2));
    } else {
      flushList();
      if (/^\d+\.\d+\s/.test(line)) {
        const match = line.match(/^(\d+\.\d+)\s+(.+)$/);
        if (match) {
          elements.push(
            <h3 key={`h3-${i}`} className="mb-2 mt-8 text-[15px] font-medium text-white/90">
              <span
                className="mr-2 font-normal"
                style={{ color: `rgba(${accentRgb}, 0.6)` }}
              >
                {match[1]}
              </span>
              {" "}
              {match[2]}
            </h3>
          );
        }
      } else {
        elements.push(
          <p key={`p-${i}`} className="my-3 text-[15px] leading-[1.85] text-white/80">
            {renderLegalInlineText(line)}
          </p>
        );
      }
    }
  });
  flushList();

  return <>{elements}</>;
}
